import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import useLiveRefresh from '../hooks/useLiveRefresh';
import { useNavigate } from 'react-router-dom';
import { Users, IndianRupee, BookOpen, Award, Building2, Briefcase, TrendingUp, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import SearchableSelect from '../components/ui/SearchableSelect';
import { fmtDMY } from '../lib/dateFormat';

const COLORS = ['#00b09b','#3b82f6','#f59e0b','#a855f7','#ef4444','#06b6d4','#22c55e','#f97316','#ec4899','#6366f1'];

// local (IST) YYYY-MM-DD — toISOString() gives the UTC date, which shifted the month boundaries by a day
const pad2 = n => String(n).padStart(2, '0');
const localYMD = (d = new Date()) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const thisMonthKey = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`; };
const monthStart = () => `${thisMonthKey()}-01`;
const monthEnd = () => { const d = new Date(); return localYMD(new Date(d.getFullYear(), d.getMonth() + 1, 0)); };

function fmt(n) { return '₹' + Number(n||0).toLocaleString('en-IN', { maximumFractionDigits: 0 }); }

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ students: 0, mechanical: 0, civil: 0, active_batches: 0, this_month_admissions: 0, this_month_ekm: 0, this_month_tvm: 0, placed: 0, placed_now: 0, hostel_occupancy: 0 });
  const [loading, setLoading] = useState(true);
  const [monthlyFees, setMonthlyFees] = useState([]);
  const [allMonthlyFees, setAllMonthlyFees] = useState([]);
  const [feeChartMode, setFeeChartMode] = useState('last');
  const [feeLastN, setFeeLastN] = useState(6);
  const [feeFromMonth, setFeeFromMonth] = useState('');
  const [feeToMonth, setFeeToMonth] = useState('');
  const [feeMonthModal, setFeeMonthModal] = useState(null); // {key, label}
  const [feeMonthData, setFeeMonthData] = useState([]);
  const [feeMonthLoading, setFeeMonthLoading] = useState(false);
  const [feeMonthSearch, setFeeMonthSearch] = useState('');
  const [packageData, setPackageData] = useState([]);
  const [pkgBatchStatus, setPkgBatchStatus] = useState([]); // [] = all, ['ongoing','upcoming','completed']
  const [pkgCentre, setPkgCentre] = useState('');
  const [ongoingBatchIds, setOngoingBatchIds] = useState(new Set());
  const [allBatches, setAllBatches] = useState([]);
  const [pkgYear, setPkgYear] = useState(''); // filter year
  const [pkgStatus, setPkgStatus] = useState(''); // '' | 'Continuing' | 'Discontinued' | 'Shifted'
  const [pkgBatch, setPkgBatch] = useState(''); // filter by batch name
  const [pkgModal, setPkgModal] = useState(null); // {name, students[]}
  const [pkgDiscipline, setPkgDiscipline] = useState('');
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [showBatches, setShowBatches] = useState(false);
  const [activeBatchList, setActiveBatchList] = useState([]);

  useEffect(() => { loadStats(); }, []);
  // keep the cards and charts current while the dashboard stays open (quiet, no spinner)
  useLiveRefresh(['students', 'fee_payments', 'batches', 'hr_status', 'placements', 'hostel_allocations'], () => loadStats(), true, 5000);

  async function loadActiveBatches() {
    const { data } = await supabase.from('batches')
      .select('id, batch_name, batch_code, discipline, centre, start_date, end_date, status')
      .eq('status', 'ongoing')
      .order('batch_name');
    const today = localYMD();
    const active = (data||[]).filter(b=>!b.end_date||b.end_date>=today);
    setActiveBatchList(active);
    setShowBatches(true);
  }

  async function loadStats() {
    try {
      // admissionDatePatched: a student counts in the month of his joining date, or of his registration date when no joining date is entered
      const _nextMonth = localYMD(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1));
      const [studRes, batchRes, certRes, placRes, hostelRes, feeRes, pkgRes, totalBatchRes] = await Promise.all([
        supabase.from('students').select('id, discipline, course_package_id, course_name, batch_id, centre, student_status', { count: 'exact', head: false }).eq('is_active', true).range(0, 2999),
        supabase.from('batches').select('id,status,start_date,batch_name').in('status',['ongoing','upcoming','completed']).limit(500).order('batch_name'),
        supabase.from('students').select('id,centre').eq('is_active', true)
          .or(`and(joining_date.gte.${monthStart()},joining_date.lt.${_nextMonth}),and(joining_date.is.null,registration_date.gte.${monthStart()},registration_date.lt.${_nextMonth})`),
        supabase.from('hr_status').select('id', {count:'exact',head:true}).eq('current_status', 'Placed'),
        supabase.from('hostel_allocations').select('id', {count:'exact',head:true}).is('vacate_date', null),
        supabase.from('fee_payments').select('amount, payment_date').gte('payment_date', new Date(new Date().getFullYear()-2+'-01-01').toISOString().slice(0,10)).order('payment_date'),
        supabase.from('course_packages').select('id, course_name, course_short_code, discipline').order('course_name'),
        supabase.from('batches').select('id', {count:'exact',head:true}),
      ]);

      // Get total count via separate count query
      const totalCount = studRes.count || studRes.data?.length || 0;
      // Load all students in pages (Supabase default limit is 1000)
      let stds = studRes.data || [];
      if (studRes.count > 3000) {
        const pages = Math.ceil(studRes.count / 3000);
        for (let p = 1; p < pages; p++) {
          const {data: extra} = await supabase.from('students')
            .select('id, discipline, course_package_id, course_name, batch_id, centre, student_status')
            .eq('is_active', true).range(p*3000, (p+1)*3000-1);
          if (extra) stds = stds.concat(extra);
        }
      }
      // everPlaced: active students with at least one placement record (same count as 'Ever Placed' in the Placement dashboard)
      const plIds = new Set();
      for (let from = 0; ; from += 1000) {
        const { data: plr, error: ple } = await supabase.from('placements').select('student_id').range(from, from + 999);
        if (ple) break;
        (plr || []).forEach(r => { if (r.student_id) plIds.add(r.student_id); });
        if (!plr || plr.length < 1000) break;
      }
      const activeIdSet = new Set(stds.map(s => s.id));
      const everPlaced = [...plIds].filter(id => activeIdSet.has(id)).length;
      const mech = stds.filter(s => (s.discipline||'').toLowerCase() === 'mechanical').length;
      const civ  = stds.filter(s => (s.discipline||'').toLowerCase() === 'civil').length;

      setStats({
        students: totalCount || stds.length, mechanical: mech, civil: civ,
        active_batches: (batchRes.data||[]).filter(b=>b.status==='ongoing').length,
        total_batches: totalBatchRes?.count || 0,
        active_batches_ekm: (batchRes.data||[]).filter(b=>{ const c=(b.centre||'').toUpperCase(); const n=(b.batch_name||'').toUpperCase(); return c.includes('EKM')||c.includes('ERNAKULAM')||c.includes('KOCHI')||n.startsWith('E'); }).length,
        active_batches_tvm: (batchRes.data||[]).filter(b=>{ const c=(b.centre||'').toUpperCase(); const n=(b.batch_name||'').toUpperCase(); return c.includes('TVM')||c.includes('TRIVANDRUM')||c.includes('THIRUVANANTHAPURAM')||n.startsWith('T'); }).length,
        this_month_admissions: certRes.data?.length || 0,
        this_month_ekm: certRes.data?.filter(s=>s.centre==='EKM').length || 0,
        this_month_tvm: certRes.data?.filter(s=>s.centre==='TVM').length || 0,
        placed: everPlaced,
        placed_now: placRes.count || 0,
        hostel_occupancy: hostelRes.count || hostelRes.data?.length || 0,
      });

      // ── Monthly fee collection ──
      const monthMap = {};
      (feeRes.data || []).forEach(f => {
        if (!f.payment_date) return;
        const d = new Date(f.payment_date);
        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
        const label = d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
        if (!monthMap[key]) monthMap[key] = { key, label, amount: 0 };
        monthMap[key].amount += Number(f.amount || 0);
      });
      const sorted = Object.values(monthMap).sort((a,b) => a.key.localeCompare(b.key));
      setAllMonthlyFees(sorted); setMonthlyFees(sorted.slice(-6)); // last 6 months

      // ── Package distribution by stream ──
      const packages = pkgRes.data || [];
      const pkgMap = {};
      const allBatchData = batchRes.data||[];
      setAllBatches(allBatchData);
      const ongoingBatchNames = new Set(allBatchData.filter(b=>b.status==='ongoing').map(b=>b.id));
      setOngoingBatchIds(ongoingBatchNames);
      // Build package lookup map for O(1) access instead of O(n) find
      const pkgById = {};
      packages.forEach(p => { pkgById[p.id] = p; });
      stds.forEach(s => {
        const pkg = pkgById[s.course_package_id];
        const pkgName = pkg?.course_short_code || pkg?.course_name || s.course_name || 'Not Assigned';
        if (!pkgName || pkgName === 'Not Assigned') return;
        const key = pkgName;
        if (!pkgMap[key]) pkgMap[key] = { name: pkgName, value: 0, ongoingCount: 0, ekmCount: 0, tvmCount: 0, ekmOngoing: 0, tvmOngoing: 0, mechCount: 0, civilCount: 0, students: [], mechStudents: [], civilStudents: [] };
        pkgMap[key].value++;
        const sCentre = (s.centre || '').toUpperCase();
        if (sCentre.includes('TVM') || sCentre.includes('TRIV')) pkgMap[key].tvmCount++; else pkgMap[key].ekmCount++;
        if (s.batch_id && ongoingBatchNames.has(s.batch_id)) {
          pkgMap[key].ongoingCount++;
          if (sCentre.includes('TVM') || sCentre.includes('TRIV')) pkgMap[key].tvmOngoing++; else pkgMap[key].ekmOngoing++;
        }
        // Determine discipline by package name
        const CIVIL_EXACT = ['CP','QS','DCE','DCEM-C','PGDCEM-C','CP+DCE','CP+DCEM','CP+QS','CP+PGDCEM-C','QA/QC-CIVIL'];
        const pkgNameLower = pkgName.toLowerCase();
        const isCivil = CIVIL_EXACT.includes(pkgName) ||
                        pkgNameLower.includes('civil') ||
                        pkgNameLower.startsWith('cp+') ||
                        pkgNameLower === 'dcem-c' || pkgNameLower === 'pgdcem-c' ||
                        pkgNameLower === 'qs' || pkgNameLower === 'dce' || pkgNameLower === 'cp';
        if (isCivil) pkgMap[key].civilCount++; else pkgMap[key].mechCount++;
        const sObj = {id:s.id,batch_id:s.batch_id,status:s.student_status,centre:(s.centre||'').toUpperCase()};
        pkgMap[key].students.push(sObj);
        if (isCivil) pkgMap[key].civilStudents.push(sObj); else pkgMap[key].mechStudents.push(sObj);
      });
      setPackageData(Object.values(pkgMap).sort((a,b) => b.value - a.value));

    } catch (e) { console.error(e); }
    setLoading(false);
  }

  const totalFees = monthlyFees.reduce((a,b) => a + b.amount, 0);

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading dashboard...</span></div>;

  const statCards = [
    { label: 'Total Students',      value: stats.students,        sub: `${stats.mechanical} Mech · ${stats.civil} Civil`, icon: Users,       color: '#3b82f6', bg: 'var(--accent-light)', route: '/students' },
    { label: 'Ongoing Batches',     value: stats.active_batches,  sub: `Total: ${stats.total_batches||0} batches`,    icon: BookOpen,    color: '#22c55e', bg: 'var(--green-light)',  route: null, onClick: loadActiveBatches },
    { label: 'This Month Admissions', value: stats.this_month_admissions, sub: `EKM: ${stats.this_month_ekm} · TVM: ${stats.this_month_tvm}`, icon: Award, color: '#f59e0b', bg: 'var(--amber-light)', route: '/students?joined=' + thisMonthKey() },
    { label: 'Ever Placed',         value: stats.placed,          sub: `Placed now: ${stats.placed_now}`,   icon: Briefcase,   color: '#a855f7', bg: 'var(--purple-light)', route: '/placement?tab=studentplace' },
    { label: 'Hostel Occupancy',    value: stats.hostel_occupancy,sub: 'Active allocations',   icon: Building2,   color: '#06b6d4', bg: 'var(--cyan-light)',   route: '/hostel' },
    { label: 'Fee Collections',     value: fmt(totalFees),        sub: 'Total collected',      icon: IndianRupee, color: '#ef4444', bg: 'var(--red-light)',    route: '/fees' },
  ];

  return (
    <>
    <div className="page">
      <div className="page-header">
        <div><h1 className="page-title">Dashboard</h1><p className="page-subtitle">Overview of institute operations</p></div>
      </div>

      <div className="stats-grid">
        {statCards.map((s, i) => (
          <div className="stat-card" key={i} onClick={() => s.onClick ? s.onClick() : s.route && navigate(s.route)}
            style={{ cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}
          >
            <div className="stat-icon" style={{ background: s.bg }}><s.icon size={18} color={s.color} /></div>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="content-grid" style={{ marginBottom: 24 }}>
        {/* Bar chart — Amount received by Month/Year */}
        <div className="card">
          <div className="card-title" style={{ marginBottom: 4 }}>
            <TrendingUp size={16} color="var(--accent)" /> Fee Collection by Month
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',marginBottom:12}}>
            <div style={{display:'flex',gap:4}}>
              {[3,6,12].map(n=>(
                <button key={n} onClick={()=>{setFeeChartMode('last');setFeeLastN(n);setMonthlyFees(allMonthlyFees.slice(-n));}} className={`btn btn-sm ${feeChartMode==='last'&&feeLastN===n?'btn-primary':'btn-secondary'}`} style={{fontSize:11,padding:'3px 8px'}}>Last {n}M</button>
              ))}
            </div>
            <div style={{display:'flex',gap:4,alignItems:'center'}}>
              <input type="month" value={feeFromMonth} onChange={e=>{setFeeFromMonth(e.target.value);setFeeChartMode('range');}} style={{fontSize:11,padding:'3px 6px',borderRadius:6,border:'1px solid var(--border)'}}/>
              <span style={{fontSize:11,color:'var(--text3)'}}>to</span>
              <input type="month" value={feeToMonth} onChange={e=>{setFeeToMonth(e.target.value);setFeeChartMode('range');if(feeFromMonth&&e.target.value){setMonthlyFees(allMonthlyFees.filter(m=>m.key>=feeFromMonth&&m.key<=e.target.value));}}} style={{fontSize:11,padding:'3px 6px',borderRadius:6,border:'1px solid var(--border)'}}/>
            </div>
            <span style={{fontSize:11,color:'var(--text3)',marginLeft:4}}>Total: <strong style={{color:'var(--accent)'}}>{fmt(monthlyFees.reduce((s,m)=>s+m.amount,0))}</strong> · {monthlyFees.length} months</span>
          </div>
          {monthlyFees.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyFees} margin={{ top: 0, right: 0, left: 10, bottom: 20 }}>
                <XAxis dataKey="label" tick={{ fill: 'var(--text3)', fontSize: 10 }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }}
                  tickFormatter={v => v >= 100000 ? `₹${(v/100000).toFixed(1)}L` : v >= 1000 ? `₹${(v/1000).toFixed(0)}K` : `₹${v}`} />
                <Tooltip
                  contentStyle={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                  formatter={(v) => [fmt(v), 'Amount Received']}
                />
                <Bar dataKey="amount" fill="var(--accent)" radius={[4,4,0,0]} style={{cursor:'pointer'}} onClick={async(data)=>{ setFeeMonthModal(data); setFeeMonthLoading(true); setFeeMonthData([]); setFeeMonthSearch(''); const [fy,fm]=data.key.split('-');const lastDay=new Date(Number(fy),Number(fm),0).getDate();const {data:rows}=await supabase.from('fee_payments').select('amount,payment_date,payment_mode,students(full_name,register_number,batch_name)').gte('payment_date',data.key+'-01').lte('payment_date',data.key+'-'+String(lastDay).padStart(2,'0')).order('payment_date'); setFeeMonthData(rows||[]); setFeeMonthLoading(false); }}>
                  {monthlyFees.map((_, i) => (
                    <Cell key={i} fill={i === monthlyFees.length - 1 ? '#f59e0b' : 'var(--accent)'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state" style={{ padding: 40 }}><p>No fee payment data yet</p></div>
          )}
        </div>

        {/* Students by Package */}
        <div className="card">
          <div className="card-title" style={{ marginBottom: 4 }}>
            <Users size={16} color="var(--accent)" /> Students by Package
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>
            Mechanical + Civil — {stats.students} total students
          </div>
          <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap',alignItems:'center'}}>
            {/* Batch status checkboxes */}
            {[{v:'ongoing',l:'Ongoing'},{v:'upcoming',l:'Upcoming'},{v:'completed',l:'Completed'}].map(({v,l})=>(
              <label key={v} style={{fontSize:11,display:'flex',alignItems:'center',gap:3,cursor:'pointer',color:'var(--text2)',whiteSpace:'nowrap'}}>
                <input type="checkbox" checked={pkgBatchStatus.includes(v)} onChange={e=>setPkgBatchStatus(prev=>e.target.checked?[...prev,v]:prev.filter(x=>x!==v))} style={{cursor:'pointer'}}/>
                {l}
              </label>
            ))}
            <span style={{color:'var(--border)'}}>|</span>
            {/* Centre buttons */}
            {['','EKM','TVM'].map(centre=>(
              <button key={centre} className={`btn btn-sm ${pkgCentre===centre?'btn-primary':'btn-secondary'}`}
                onClick={()=>setPkgCentre(pkgCentre===centre&&centre!==''?'':centre)}
                style={{fontSize:10,padding:'2px 6px'}}>{centre||'All'}</button>
            ))}
            <span style={{color:'var(--border)'}}>|</span>
            {/* Dropdowns */}
            <SearchableSelect
              value={pkgYear}
              onChange={v=>setPkgYear(v||'')}
              options={[...new Set(allBatches.filter(b=>b.start_date).map(b=>new Date(b.start_date).getFullYear()))].sort((a,b)=>b-a).map(y=>({value:String(y),label:String(y)}))}
              placeholder="Year"
              searchPlaceholder="Search year..."
              style={{minWidth:80,fontSize:10}}
            />
            <select value={pkgStatus} onChange={e=>setPkgStatus(e.target.value)} style={{fontSize:10,padding:'2px 4px',borderRadius:4,border:'1px solid var(--border)',maxWidth:90}}>
              <option value="">Status</option>
              <option value="Continuing">Continuing</option>
              <option value="Passed Out">Passed Out</option>
              <option value="Discontinued">Discontinued</option>
              <option value="Shifted">Shifted</option>
            </select>
            <SearchableSelect
              value={pkgBatch}
              onChange={v=>setPkgBatch(v||'')}
              options={[...new Set(allBatches.map(b=>b.batch_name||b.id).filter(Boolean))].sort().map(bn=>({value:bn,label:bn}))}
              placeholder="Batch"
              searchPlaceholder="Search batch..."
              style={{minWidth:90,fontSize:10}}
            />
            {(pkgBatchStatus.length||pkgYear||pkgStatus||pkgBatch||pkgCentre)&&<button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'1px 6px'}} onClick={()=>{setPkgBatchStatus([]);setPkgYear('');setPkgStatus('');setPkgBatch('');setPkgCentre('');}}>✕ Reset</button>}
          </div>
          {packageData.length > 0 ? (
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
              {[{disc:'mech',label:'⚙️ Mechanical',color:'#3b82f6',countKey:'mechCount'},{disc:'civil',label:'🏗️ Civil',color:'#16a34a',countKey:'civilCount'}].map(({disc,label,color,countKey})=>{
                // Build batch ID sets for year filter
                const yearBatchIds = pkgYear
                  ? new Set(allBatches.filter(b=>b.start_date&&new Date(b.start_date).getFullYear()===Number(pkgYear)).map(b=>b.id))
                  : null;
                let data = packageData.filter(p=>p[countKey]>0);
                // Apply all filters using student-level data
                data = data.map(p => {
                  let students = (disc==='mech' ? p.mechStudents : p.civilStudents) || [];
                  // Batch status filter - support multi-select
                  if (pkgBatchStatus.length > 0) {
                    const allowedBids = new Set(allBatches.filter(b=>pkgBatchStatus.includes(b.status)).map(b=>b.id));
                    students = students.filter(s=>allowedBids.has(s.batch_id));
                  }
                  if (pkgYear && yearBatchIds) students = students.filter(s=>yearBatchIds.has(s.batch_id));
                  if (pkgStatus) students = students.filter(s=>s.status===pkgStatus);
                  if (pkgBatch) { const bBids=new Set(allBatches.filter(b=>(b.batch_name||b.id)===pkgBatch).map(b=>b.id)); students=students.filter(s=>bBids.has(s.batch_id)); }
                  if (pkgCentre==='EKM') students = students.filter(s=>!s.centre||s.centre==='EKM'||s.centre.includes('EKM'));
                  else if (pkgCentre==='TVM') students = students.filter(s=>s.centre.includes('TVM')||s.centre.includes('TRIV'));
                  return {...p, value: students.length, filteredStudents: students};
                });
                data = data.filter(p=>p.value>0).sort((a,b)=>b.value-a.value);
                const total = data.reduce((s,p)=>s+p.value,0);
                return (
                  <div key={disc}>
                    <div style={{fontWeight:700,fontSize:12,color,marginBottom:8,borderBottom:`2px solid ${color}`,paddingBottom:4}}>
                      {label} — {total}
                    </div>
                    <div style={{maxHeight:220,overflowY:'auto'}}>
                      {data.map((pkg,i)=>{
                        const pct = total>0?((pkg.value/total)*100).toFixed(1):0;
                        return (
                          <div key={i} style={{marginBottom:7,cursor:'pointer'}} onClick={async()=>{
                      const ids = (pkg.filteredStudents||pkg.students||[]).map(s=>s.id||s);
                      if(!ids.length){setPkgModal({name:pkg.name,students:[]});return;}
                      const chunks=[];
                      for(let i=0;i<ids.length;i+=500) chunks.push(ids.slice(i,i+500));
                      const results=await Promise.all(chunks.map(chunk=>supabase.from('students').select('full_name,register_number,batch_name,student_status,centre').in('id',chunk).order('register_number','ascending')));
                      setPkgModal({name:pkg.name,students:results.flatMap(r=>r.data||[])});
                    }}>
                            <div style={{display:'flex',justifyContent:'space-between',marginBottom:2}}>
                              <span style={{fontSize:11,fontWeight:500,maxWidth:'68%',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'var(--text1)'}} title={pkg.name}>{pkg.name}</span>
                              <span style={{fontSize:11,color:'var(--text3)',whiteSpace:'nowrap',marginLeft:4}}>{pkg.value} <b style={{color}}>({pct}%)</b></span>
                            </div>
                            <div style={{height:6,background:'var(--border)',borderRadius:3}}>
                              <div style={{height:'100%',width:pct+'%',background:color,borderRadius:3}}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state" style={{ padding: 40 }}><p>No package data yet</p></div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-title"><AlertTriangle size={16} color="var(--amber)" /> Quick Actions</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { label: 'Add Student', href: '/students', color: 'btn-primary' },
            { label: 'Mark Attendance', href: '/attendance', color: 'btn-secondary' },
            { label: 'Record Payment', href: '/fees', color: 'btn-secondary' },
            { label: 'New Exam Request', href: '/exams', color: 'btn-secondary' },
            { label: 'Hostel Allocation', href: '/hostel', color: 'btn-secondary' },
          ].map(a => (
            <a key={a.href} href={a.href} className={`btn ${a.color}`}>{a.label}</a>
          ))}
        </div>
      </div>
    </div>

      {showBatches && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowBatches(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h3>📚 Active Batches ({activeBatchList.length})</h3>
              <button className="modal-close" onClick={()=>setShowBatches(false)}>×</button>
            </div>
            <div className="modal-body" style={{maxHeight:'70vh',overflowY:'auto'}}>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Batch Name <span style={{fontSize:10,color:'var(--text3)',fontWeight:400}}>(click to filter students)</span></th>
                    <th>Code</th>
                    <th>Discipline</th>
                    <th>Centre</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                  </tr>
                </thead>
                <tbody>
                  {activeBatchList.length === 0
                    ? <tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No active batches found</td></tr>
                    : activeBatchList.map((b,i) => (
                      <tr key={b.id} style={{cursor:'pointer'}} title="Click to view students in this batch"
                        onClick={()=>{ setShowBatches(false); navigate('/students?batch='+encodeURIComponent(b.batch_name)); }}>
                        <td style={{color:'var(--text3)',fontSize:12}}>{i+1}</td>
                        <td style={{fontWeight:600}}>{b.batch_name}</td>
                        <td style={{fontFamily:'monospace',fontSize:12,color:'var(--accent)'}}>{b.batch_code||'—'}</td>
                        <td><span className={`badge ${b.discipline==='civil'?'badge-blue':'badge-amber'}`} style={{fontSize:10}}>{b.discipline||'—'}</span></td>
                        <td style={{fontSize:12}}>{b.centre||'—'}</td>
                        <td style={{fontSize:12}}>{fmtDMY(b.start_date)||'—'}</td>
                        <td style={{fontSize:12}}>{fmtDMY(b.end_date)||'—'}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={()=>setShowBatches(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {feeMonthModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}} onClick={e=>e.target===e.currentTarget&&setFeeMonthModal(null)}>
          <div style={{background:'var(--bg)',borderRadius:12,width:'100%',maxWidth:700,maxHeight:'80vh',display:'flex',flexDirection:'column',boxShadow:'0 8px 32px rgba(0,0,0,0.2)'}}>
            <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontWeight:700,fontSize:15}}>💰 Fee Collections — {feeMonthModal.label}</div>
                <div style={{fontSize:12,color:'var(--text3)',marginTop:2}}>{feeMonthData.filter(r=>!feeMonthSearch||(r.students?.full_name||'').toLowerCase().includes(feeMonthSearch.toLowerCase())||(r.students?.register_number||'').toLowerCase().includes(feeMonthSearch.toLowerCase())||String(r.amount||'').includes(feeMonthSearch)||(r.students?.batch_name||'').toLowerCase().includes(feeMonthSearch.toLowerCase())||(r.payment_mode||'').toLowerCase().includes(feeMonthSearch.toLowerCase())).length} of {feeMonthData.length} payments · Total: ₹{feeMonthData.reduce((s,r)=>s+Number(r.amount||0),0).toLocaleString('en-IN')}</div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <input value={feeMonthSearch} onChange={e=>setFeeMonthSearch(e.target.value)} placeholder="Search student/reg no..." style={{padding:'6px 10px',borderRadius:6,border:'1px solid var(--border)',fontSize:12,width:200}}/>
                <button style={{background:'none',border:'none',fontSize:18,cursor:'pointer',color:'var(--text3)'}} onClick={()=>setFeeMonthModal(null)}>✕</button>
              </div>
            </div>
            <div style={{overflowY:'auto',flex:1}}>
              {feeMonthLoading ? (
                <div style={{padding:40,textAlign:'center',color:'var(--text3)'}}>Loading...</div>
              ) : feeMonthData.length===0 ? (
                <div style={{padding:40,textAlign:'center',color:'var(--text3)'}}>No payments found</div>
              ) : (
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead style={{position:'sticky',top:0,background:'#e6f7f5',zIndex:10}}>
                    <tr>
                      <th style={{padding:'10px 14px',textAlign:'left',background:'#e6f7f5'}}>#</th>
                      <th style={{padding:'10px 14px',textAlign:'left',background:'#e6f7f5'}}>Student</th>
                      <th style={{padding:'10px 14px',textAlign:'left',background:'#e6f7f5'}}>Batch</th>
                      <th style={{padding:'10px 14px',textAlign:'left',background:'#e6f7f5'}}>Date</th>
                      <th style={{padding:'10px 14px',textAlign:'right',background:'#e6f7f5'}}>Amount</th>
                      <th style={{padding:'10px 14px',textAlign:'left',background:'#e6f7f5'}}>Mode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feeMonthData.filter(r=>!feeMonthSearch||(r.students?.full_name||'').toLowerCase().includes(feeMonthSearch.toLowerCase())||(r.students?.register_number||'').toLowerCase().includes(feeMonthSearch.toLowerCase())||String(r.amount||'').includes(feeMonthSearch)||(r.students?.batch_name||'').toLowerCase().includes(feeMonthSearch.toLowerCase())||(r.payment_mode||'').toLowerCase().includes(feeMonthSearch.toLowerCase())).map((r,i)=>(
                      <tr key={i} style={{borderTop:'1px solid var(--border)',background:i%2===0?'var(--bg)':'var(--bg2)'}}>
                        <td style={{padding:'8px 14px',color:'var(--text3)'}}>{i+1}</td>
                        <td style={{padding:'8px 14px'}}>
                          <div style={{fontWeight:500}}>{r.students?.full_name||'—'}</div>
                          <div style={{fontSize:11,color:'var(--text3)'}}>{r.students?.register_number||''}</div>
                        </td>
                        <td style={{padding:'8px 14px',color:'var(--text3)'}}>{r.students?.batch_name||'—'}</td>
                        <td style={{padding:'8px 14px',color:'var(--text3)'}}>{r.payment_date?new Date(r.payment_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}):'-'}</td>
                        <td style={{padding:'8px 14px',textAlign:'right',fontWeight:600,color:'var(--accent)'}}>₹{Number(r.amount||0).toLocaleString('en-IN')}</td>
                        <td style={{padding:'8px 14px',color:'var(--text3)',fontSize:11}}>{r.payment_mode||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {pkgModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setPkgModal(null)}>
          <div className="modal" style={{maxWidth:700}}>
            <div className="modal-header">
              <span className="modal-title">📦 {pkgModal.name} — {pkgModal.students.length} Students</span>
              <button className="btn btn-secondary btn-sm" style={{marginLeft:'auto',marginRight:8}} onClick={()=>{
                const w=window.open('','_blank');
                const rows=pkgModal.students.map((s,i)=>`<tr><td>${i+1}</td><td>${s.register_number||''}</td><td>${s.full_name||''}</td><td>${s.batch_name||''}</td><td>${s.centre||'EKM'}</td><td>${s.student_status||''}</td></tr>`).join('');
                w.document.write(`<!DOCTYPE html><html><head><title>${pkgModal.name}</title><style>body{font-family:Arial,sans-serif;font-size:12px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}th{background:#1a3a6b;color:#fff}tr:nth-child(even){background:#f5f5f5}h2{font-size:14px}@media print{button{display:none}}</style></head><body><h2>📦 ${pkgModal.name} — ${pkgModal.students.length} Students</h2><table><thead><tr><th>#</th><th>Reg No</th><th>Name</th><th>Batch</th><th>Centre</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table><br/><button onclick="window.print()">🖨️ Print</button><script>window.onload=()=>window.print();</scr\`+'ipt></body></html>`);w.document.close();
              }}>🖨️ Print</button>
              <button className="btn btn-ghost btn-sm" onClick={()=>setPkgModal(null)}>✕</button>
            </div>
            <div className="modal-body" style={{maxHeight:'60vh',overflowY:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead style={{position:'sticky',top:0,background:'var(--accent-light)'}}>
                  <tr>
                    <th style={{padding:'6px 8px',textAlign:'left'}}>#</th>
                    <th style={{padding:'6px 8px',textAlign:'left'}}>Reg No</th>
                    <th style={{padding:'6px 8px',textAlign:'left'}}>Name</th>
                    <th style={{padding:'6px 8px',textAlign:'left'}}>Batch</th>
                    <th style={{padding:'6px 8px',textAlign:'left'}}>Centre</th>
                    <th style={{padding:'6px 8px',textAlign:'left'}}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pkgModal.students.map((s,i)=>(
                    <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
                      <td style={{padding:'5px 8px',color:'var(--text3)'}}>{i+1}</td>
                      <td style={{padding:'5px 8px',fontFamily:'monospace',fontSize:11,color:'var(--accent)'}}>{s.register_number}</td>
                      <td style={{padding:'5px 8px',fontWeight:500}}>{s.full_name}</td>
                      <td style={{padding:'5px 8px'}}>{s.batch_name}</td>
                      <td style={{padding:'5px 8px'}}>{s.centre||'EKM'}</td>
                      <td style={{padding:'5px 8px'}}><span style={{fontSize:10,padding:'1px 6px',borderRadius:10,background:s.student_status==='Continuing'?'rgba(34,197,94,0.1)':'rgba(239,68,68,0.1)',color:s.student_status==='Continuing'?'var(--green)':'var(--red)'}}>{s.student_status||'Continuing'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setPkgModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
